import { useEffect, useRef, useState } from "react";
import { Animated, Easing, Modal, Platform, Pressable, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { AudienceIcon, CheckIcon, CleanAudioIcon } from "@/design/icons";
import { Muted, Overline, Text } from "@/design/Text";
import { palette, radius, space } from "@/design/tokens";
import { isCleanAudioTrack } from "@/playback/AudioFeedChip";
import type { VideoSurfaceAudioTrack } from "@/playback/players/VideoSurface";

// Same vocabulary as the web skin's videojs Speed submenu.
const RATE_OPTIONS = [
  { label: "0.5×", value: 0.5 },
  { label: "0.75×", value: 0.75 },
  { label: "Normal", value: 1 },
  { label: "1.25×", value: 1.25 },
  { label: "1.5×", value: 1.5 },
  { label: "2×", value: 2 }
] as const;

export type PlaybackSettingsSheetProps = {
  visible: boolean;
  onClose: () => void;
  /** Audio section renders only when there is a real choice (2+ tracks). */
  audioTracks: VideoSurfaceAudioTrack[];
  selectedAudioTrack: string | null;
  onSelectAudioTrack: (id: string) => void;
  /** null hides the Speed section (casting has no rate control). */
  playbackRate: number | null;
  onSelectPlaybackRate: (rate: number) => void;
};

/**
 * Native settings surface — bottom sheet, per docs/player-chrome-spec.md:
 * same sections/labels as the web skin's videojs menu, mobile container.
 */
export function PlaybackSettingsSheet({
  visible,
  onClose,
  audioTracks,
  selectedAudioTrack,
  onSelectAudioTrack,
  playbackRate,
  onSelectPlaybackRate
}: PlaybackSettingsSheetProps) {
  const insets = useSafeAreaInsets();
  const progress = useRef(new Animated.Value(0)).current;
  const [mounted, setMounted] = useState(visible);

  useEffect(() => {
    if (visible) setMounted(true);
    Animated.timing(progress, {
      toValue: visible ? 1 : 0,
      duration: visible ? 240 : 180,
      easing: visible ? Easing.out(Easing.cubic) : Easing.in(Easing.cubic),
      useNativeDriver: Platform.OS !== "web"
    }).start(({ finished }) => {
      if (finished && !visible) setMounted(false);
    });
  }, [progress, visible]);

  if (!mounted) return null;

  const showAudio = audioTracks.length > 1;
  const showSpeed = playbackRate != null;

  return (
    <Modal transparent statusBarTranslucent visible onRequestClose={onClose}>
      <View style={styles.root}>
        <Animated.View style={[StyleSheet.absoluteFill, { opacity: progress }]}>
          <Pressable
            accessibilityLabel="Close settings"
            style={[StyleSheet.absoluteFill, styles.backdrop]}
            onPress={onClose}
          />
        </Animated.View>

        <Animated.View
          style={[
            styles.panel,
            {
              paddingBottom: Math.max(insets.bottom, space.lg) + space.sm,
              opacity: progress,
              transform: [
                {
                  translateY: progress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [56, 0]
                  })
                }
              ]
            }
          ]}
        >
          <View style={styles.grabber} />

          {showAudio ? (
            <View style={styles.section}>
              <Overline color={palette.muted} style={styles.sectionLabel}>
                Audio
              </Overline>
              {audioTracks.map((track) => {
                const selected = track.id === selectedAudioTrack;
                return (
                  <Pressable
                    key={track.id}
                    accessibilityRole="radio"
                    accessibilityState={{ selected }}
                    accessibilityLabel={track.label}
                    onPress={() => {
                      onSelectAudioTrack(track.id);
                      onClose();
                    }}
                    style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
                  >
                    <View style={styles.audioLabel}>
                      {isCleanAudioTrack(track) ? (
                        <CleanAudioIcon
                          size={18}
                          color={selected ? palette.text : palette.muted}
                        />
                      ) : (
                        <AudienceIcon
                          size={18}
                          color={selected ? palette.text : palette.muted}
                        />
                      )}
                      <Text
                        variant="body"
                        color={selected ? palette.text : palette.textSoft}
                      >
                        {track.label}
                      </Text>
                    </View>
                    {selected ? <CheckIcon size={18} color={palette.text} /> : null}
                  </Pressable>
                );
              })}
            </View>
          ) : null}

          {showAudio && showSpeed ? <View style={styles.separator} /> : null}

          {showSpeed ? (
            <View style={styles.section}>
              <Overline color={palette.muted} style={styles.sectionLabel}>
                Speed
              </Overline>
              <View style={styles.chipRow}>
                {RATE_OPTIONS.map((option) => {
                  const selected = option.value === playbackRate;
                  return (
                    <Pressable
                      key={option.value}
                      accessibilityRole="radio"
                      accessibilityState={{ selected }}
                      accessibilityLabel={`Playback speed ${option.label}`}
                      onPress={() => onSelectPlaybackRate(option.value)}
                      style={({ pressed }) => [
                        styles.chip,
                        selected && styles.chipSelected,
                        pressed && { opacity: 0.7 }
                      ]}
                    >
                      <Muted
                        variant="smallStrong"
                        color={selected ? palette.text : palette.textSoft}
                      >
                        {option.label}
                      </Muted>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          ) : null}
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: "flex-end"
  },
  backdrop: {
    backgroundColor: palette.scrim
  },
  panel: {
    alignSelf: "center",
    backgroundColor: palette.surfaceOverlay,
    borderTopLeftRadius: radius.xl,
    borderTopRightRadius: radius.xl,
    maxWidth: 392,
    paddingHorizontal: space.lg,
    paddingTop: space.sm,
    width: "100%"
  },
  grabber: {
    alignSelf: "center",
    backgroundColor: palette.hairlineStrong,
    borderRadius: radius.pill,
    height: 4,
    marginBottom: space.md,
    width: 36
  },
  section: {
    paddingVertical: space.xs
  },
  sectionLabel: {
    paddingBottom: space.xs,
    paddingHorizontal: space.sm,
    paddingTop: space.xs
  },
  row: {
    alignItems: "center",
    borderRadius: radius.md,
    flexDirection: "row",
    justifyContent: "space-between",
    minHeight: 50,
    paddingHorizontal: space.sm
  },
  rowPressed: {
    backgroundColor: palette.coolWash
  },
  audioLabel: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.md
  },
  separator: {
    backgroundColor: palette.hairline,
    height: 1,
    marginVertical: space.sm
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    paddingHorizontal: space.sm,
    paddingVertical: space.xs
  },
  chip: {
    backgroundColor: palette.coolWashSubtle,
    borderRadius: radius.pill,
    minHeight: 36,
    justifyContent: "center",
    paddingHorizontal: 10
  },
  chipSelected: {
    backgroundColor: palette.coolWashHover
  }
});
